import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container, Row, Col, Card, CardBody, CardTitle, Button, Table, Badge, Modal, ModalHeader,
  ModalBody, Form, FormGroup, Label, Input, Alert, Pagination, PaginationItem, PaginationLink,
  InputGroup, Collapse
} from 'reactstrap';
import { FaListAlt, FaSearch, FaFilter, FaChevronDown, FaChevronUp, FaEye, FaSync, FaActivity, FaClock, FaCheckCircle, FaTimesCircle, FaServer } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import API from '../services/api';
import vkbeautify from 'vkbeautify';

const EppLogs = () => {
  const { user: currentUser } = useAuth();
  const location = useLocation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);

  // Filters
  const [filters, setFilters] = useState({
    registrar_name: '',
    command: '',
    success: '',
    account_id: '',
    object_name: '',
    date_from: '',
    date_to: '',
    showFilters: false
  });

  // Detail modal
  const [selectedLog, setSelectedLog] = useState(null);
  const [logDetailModal, setLogDetailModal] = useState(false);

  // Stats
  const [stats, setStats] = useState(null);

  const isAdmin = currentUser?.role === 'admin';

  // Load URL query parameters on mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const registrarName = searchParams.get('registrar_name');

    if (registrarName) {
      setFilters(prev => ({
        ...prev,
        registrar_name: registrarName,
        showFilters: true
      }));
    }
  }, [location.search]);

  useEffect(() => {
    loadLogs();
    loadStats();
  }, [currentPage, filters]);

  const loadLogs = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        page: currentPage.toString(),
        per_page: '50',
        ...filters
      });

      const response = await API.get(`/epp/logs?${params}`);
      setLogs(response.data.logs || []);

      if (response.data.pagination) {
        setTotalPages(response.data.pagination.pages);
        setTotalLogs(response.data.pagination.total);
      }

    } catch (err) {
      setError('Failed to load EPP logs: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await API.get('/epp/logs/stats');
      setStats(response.data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const loadLogDetail = async (logId) => {
    try {
      const response = await API.get(`/epp/logs/${logId}`);
      setSelectedLog(response.data.log);
      setLogDetailModal(true);
    } catch (err) {
      setError('Failed to load log detail: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  const clearFilters = () => {
    setFilters({
      registrar_name: '',
      command: '',
      success: '',
      account_id: '',
      object_name: '',
      date_from: '',
      date_to: '',
      showFilters: true
    });
    setCurrentPage(1);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return timestamp;
    }
  };

  const getStatusBadge = (success) => {
    return success ? (
      <Badge className="bg-dark text-success border border-success">
        <FaCheckCircle size={12} className="me-1" />
        Success
      </Badge>
    ) : (
      <Badge className="bg-dark text-danger border border-danger">
        <FaTimesCircle size={12} className="me-1" />
        Failed
      </Badge>
    );
  };

  const getConnectionTypeBadge = (type) => {
    // Handle both string and numeric connection types
    const numericToString = {
      1: 'connect',
      2: 'login',
      3: 'command',
      4: 'logout',
      5: 'error'
    };

    // Convert numeric type to string if needed
    const typeString = typeof type === 'number' ? numericToString[type] : type;

    const types = {
      'connect': { color: 'info', icon: '🔌', label: 'CONNECT' },
      'login': { color: 'primary', icon: '🔑', label: 'LOGIN' },
      'command': { color: 'secondary', icon: '⚡', label: 'COMMAND' },
      'logout': { color: 'warning', icon: '🚪', label: 'LOGOUT' },
      'error': { color: 'danger', icon: '❌', label: 'ERROR' }
    };

    const config = types[typeString] || { color: 'secondary', icon: '❓', label: 'UNKNOWN' };

    return (
      <Badge className={`bg-dark text-${config.color} border border-${config.color}`}>
        {config.icon} {config.label}
      </Badge>
    );
  };

  const formatXml = (xmlString) => {
    if (!xmlString) return 'No data';

    try {
      // Try to format with vkbeautify
      return vkbeautify.xml(xmlString, 2);
    } catch (error) {
      // If vkbeautify fails, try native browser formatting
      try {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlString, 'application/xml');
        const serializer = new XMLSerializer();
        const formatted = serializer.serializeToString(xmlDoc);

        // Basic indentation if serializer doesn't format
        return formatted === xmlString ? xmlString : formatted;
      } catch (nativeError) {
        // If all fails, return original string
        return xmlString;
      }
    }
  };


  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="page-heading">
              <FaListAlt className="page-heading-icon" />
              EPP Logs
            </h2>
            <Button color="secondary" size="sm" onClick={loadLogs} disabled={loading}>
              <FaSync size={14} className="me-1" />
              {loading ? 'Loading...' : 'Refresh'}
            </Button>
          </div>
        </Col>
      </Row>

      {error && <Alert color="danger" dismissible onDismiss={() => setError('')}>{error}</Alert>}

      {/* Statistics Cards */}
      <Row className="mb-4">
        <Col md="3" className="mb-3">
          <Card>
            <CardBody>
              <CardTitle tag="h6" className="text-muted mb-1">Total Logs</CardTitle>
              <h3 className="mb-0">{stats?.stats?.total_logs?.toLocaleString() || 0}</h3>
            </CardBody>
          </Card>
        </Col>
        <Col md="3" className="mb-3">
          <Card>
            <CardBody>
              <CardTitle tag="h6" className="text-muted mb-1">
                <FaCheckCircle className="text-success" />
                Success Rate
              </CardTitle>
              <h3 className="mb-0 text-success">{stats?.stats?.success_rate || 0}%</h3>
            </CardBody>
          </Card>
        </Col>
        <Col md="3" className="mb-3">
          <Card>
            <CardBody>
              <CardTitle tag="h6" className="text-muted mb-1">
                <FaClock className="text-warning" />
                Avg Duration
              </CardTitle>
              <h3 className="mb-0 text-warning">{stats?.stats?.avg_duration_ms || 0}ms</h3>
            </CardBody>
          </Card>
        </Col>
        <Col md="3" className="mb-3">
          <Card>
            <CardBody>
              <CardTitle tag="h6" className="text-muted mb-1">
                <FaServer className="text-primary" />
                Registrars
              </CardTitle>
              <h3 className="mb-0 text-primary">{stats?.stats?.unique_registrars || 0}</h3>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="mb-4">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center">
            <h5>
              <FaFilter className="me-2" />
              Filters
            </h5>
            <Button
              color="link"
              onClick={() => handleFilterChange('showFilters', !filters.showFilters)}
              className="p-0"
            >
              {filters.showFilters ? <FaChevronUp /> : <FaChevronDown />}
            </Button>
          </div>
          <Collapse isOpen={filters.showFilters}>
            <hr />
            <Row>
              <Col md="3">
                <FormGroup>
                  <Label>Registrar</Label>
                  <InputGroup>
                    <Input
                      type="text"
                      placeholder="Search registrars..."
                      value={filters.registrar_name}
                      onChange={(e) => handleFilterChange('registrar_name', e.target.value)}
                      className="bg-secondary border-secondary text-white"
                    />
                    <Button color="primary">
                      <FaSearch />
                    </Button>
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label>Command</Label>
                  <Input
                    type="text"
                    placeholder="Search commands..."
                    value={filters.command}
                    onChange={(e) => handleFilterChange('command', e.target.value)}
                    className="bg-secondary border-secondary text-white"
                  />
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label>Object</Label>
                  <Input
                    type="text"
                    placeholder="Search objects..."
                    value={filters.object_name}
                    onChange={(e) => handleFilterChange('object_name', e.target.value)}
                    className="bg-secondary border-secondary text-white"
                  />
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label>Status</Label>
                  <Input
                    type="select"
                    value={filters.success}
                    onChange={(e) => handleFilterChange('success', e.target.value)}
                    className="bg-secondary border-secondary text-white form-control"
                  >
                    <option value="">All</option>
                    <option value="1">Success</option>
                    <option value="0">Failed</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md="4">
                <FormGroup>
                  <Label>From Date</Label>
                  <Input
                    type="datetime-local"
                    value={filters.date_from}
                    onChange={(e) => handleFilterChange('date_from', e.target.value)}
                    className="bg-secondary border-secondary text-white"
                  />
                </FormGroup>
              </Col>
              <Col md="4">
                <FormGroup>
                  <Label>To Date</Label>
                  <Input
                    type="datetime-local"
                    value={filters.date_to}
                    onChange={(e) => handleFilterChange('date_to', e.target.value)}
                    className="bg-secondary border-secondary text-white"
                  />
                </FormGroup>
              </Col>
              <Col md="4">
                <FormGroup>
                  <Label>&nbsp;</Label>
                  <div>
                    <Button
                      color="secondary"
                      onClick={clearFilters}
                    >
                      Clear Filters
                    </Button>
                  </div>
                </FormGroup>
              </Col>
            </Row>
          </Collapse>
        </CardBody>
      </Card>

      {/* EPP Logs Table */}
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>EPP Logs ({totalLogs})</h5>
          </div>

          {loading ? (
            <div className="text-center py-4">Loading EPP logs...</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-4">No EPP logs found</div>
          ) : (
            <>
              <div className="table-responsive">
                <Table striped>
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Registrar</th>
                    <th>Type</th>
                    <th>Command</th>
                    <th>Object</th>
                    <th>Status</th>
                    <th>Duration</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => (
                    <tr key={log.log_id}>
                      <td>
                        <small>{formatTimestamp(log.timestamp)}</small>
                      </td>
                      <td>
                        <strong>{log.registrar_name}</strong>
                        {isAdmin && (
                          <small className="d-block text-muted">
                            Account: {log.account_id}
                          </small>
                        )}
                      </td>
                      <td>
                        {getConnectionTypeBadge(log.connection_type)}
                      </td>
                      <td>
                        <code className="small">{log.command}</code>
                        {log.cltrid && (
                          <small className="d-block text-muted">
                            ID: {log.cltrid}
                          </small>
                        )}
                      </td>
                      <td>
                        {log.object_name ? (
                          <span className="small">{log.object_name}</span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        {getStatusBadge(log.success)}
                        {log.status_code && (
                          <small className="d-block text-muted">
                            Code: {log.status_code}
                          </small>
                        )}
                      </td>
                      <td>
                        {log.duration_ms ? (
                          <span>{log.duration_ms}ms</span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        <Button
                          color="outline-info"
                          size="sm"
                          onClick={() => loadLogDetail(log.log_id)}
                          title="View Details"
                        >
                          <FaEye size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <PaginationItem disabled={currentPage <= 1}>
                      <PaginationLink previous onClick={() => setCurrentPage(currentPage - 1)} />
                    </PaginationItem>

                    {/* First page */}
                    {currentPage > 3 && (
                      <>
                        <PaginationItem>
                          <PaginationLink onClick={() => setCurrentPage(1)}>1</PaginationLink>
                        </PaginationItem>
                        {currentPage > 4 && (
                          <PaginationItem disabled>
                            <PaginationLink>...</PaginationLink>
                          </PaginationItem>
                        )}
                      </>
                    )}

                    {/* Pages around current page */}
                    {[...Array(5)].map((_, i) => {
                      const pageNum = currentPage - 2 + i;
                      if (pageNum >= 1 && pageNum <= totalPages) {
                        return (
                          <PaginationItem key={pageNum} active={currentPage === pageNum}>
                            <PaginationLink onClick={() => setCurrentPage(pageNum)}>
                              {pageNum}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      }
                      return null;
                    })}

                    {/* Last page */}
                    {currentPage < totalPages - 2 && (
                      <>
                        {currentPage < totalPages - 3 && (
                          <PaginationItem disabled>
                            <PaginationLink>...</PaginationLink>
                          </PaginationItem>
                        )}
                        <PaginationItem>
                          <PaginationLink onClick={() => setCurrentPage(totalPages)}>{totalPages}</PaginationLink>
                        </PaginationItem>
                      </>
                    )}

                    <PaginationItem disabled={currentPage >= totalPages}>
                      <PaginationLink next onClick={() => setCurrentPage(currentPage + 1)} />
                    </PaginationItem>
                  </Pagination>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {/* Log Detail Modal */}
      <Modal isOpen={logDetailModal} toggle={() => setLogDetailModal(false)} size="xl" className="modal-dark">
        <ModalHeader toggle={() => setLogDetailModal(false)} className="bg-dark text-white border-secondary">
          <FaSearch className="me-2" />
          EPP Log Detail
        </ModalHeader>
        <ModalBody className="bg-dark text-white">
          {selectedLog && (
            <div>
              {/* Log metadata */}
              <Row className="mb-4">
                <Col md={6}>
                  <h6>Log Information</h6>
                  <table className="table table-sm table-dark">
                    <tbody>
                      <tr>
                        <td><strong>Timestamp:</strong></td>
                        <td>{formatTimestamp(selectedLog.timestamp)}</td>
                      </tr>
                      <tr>
                        <td><strong>Registrar:</strong></td>
                        <td>{selectedLog.registrar_name}</td>
                      </tr>
                      <tr>
                        <td><strong>Command:</strong></td>
                        <td><code>{selectedLog.command}</code></td>
                      </tr>
                      <tr>
                        <td><strong>Type:</strong></td>
                        <td>{getConnectionTypeBadge(selectedLog.connection_type)}</td>
                      </tr>
                      <tr>
                        <td><strong>Status:</strong></td>
                        <td>{getStatusBadge(selectedLog.success)}</td>
                      </tr>
                    </tbody>
                  </table>
                </Col>
                <Col md={6}>
                  <h6>Technical Details</h6>
                  <table className="table table-sm table-dark">
                    <tbody>
                      <tr>
                        <td><strong>Client TRID:</strong></td>
                        <td><code>{selectedLog.cltrid || 'N/A'}</code></td>
                      </tr>
                      <tr>
                        <td><strong>Server TRID:</strong></td>
                        <td><code>{selectedLog.svtrid || 'N/A'}</code></td>
                      </tr>
                      <tr>
                        <td><strong>Status Code:</strong></td>
                        <td>{selectedLog.status_code || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td><strong>Duration:</strong></td>
                        <td>{selectedLog.duration_ms ? `${selectedLog.duration_ms}ms` : 'N/A'}</td>
                      </tr>
                      <tr>
                        <td><strong>Object Name:</strong></td>
                        <td>{selectedLog.object_name || 'N/A'}</td>
                      </tr>
                    </tbody>
                  </table>
                </Col>
              </Row>

              {/* Request/Response */}
              <Row>
                <Col md={6}>
                  <h6>Request</h6>
                  <pre className="bg-secondary p-3 rounded text-light" style={{ height: '300px', overflow: 'auto', fontSize: '12px', lineHeight: '1.4' }}>
                    {formatXml(selectedLog.request)}
                  </pre>
                </Col>
                <Col md={6}>
                  <h6>Response</h6>
                  <pre className="bg-secondary p-3 rounded text-light" style={{ height: '300px', overflow: 'auto', fontSize: '12px', lineHeight: '1.4' }}>
                    {formatXml(selectedLog.response)}
                  </pre>
                </Col>
              </Row>

              {/* Status message */}
              {selectedLog.status_message && (
                <Row className="mt-3">
                  <Col>
                    <h6>Status Message</h6>
                    <div className="alert alert-info">
                      {selectedLog.status_message}
                    </div>
                  </Col>
                </Row>
              )}
            </div>
          )}
        </ModalBody>
      </Modal>
    </Container>
  );
};

export default EppLogs;